import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, CaseSensitive } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TidyDepartmentNamesModal = ({ open, onClose, onDone }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    axios.get(`${API}/products/department-names/tidy-preview`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Could not load department names"); onClose(); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/products/department-names/tidy-apply`);
      toast.success(`Tidied ${r.data.departments_changed} departments · ${r.data.products_updated} products updated`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to tidy names");
    } finally {
      setBusy(false);
    }
  };

  const changes = data?.changes || [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="tidy-dept-names-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CaseSensitive className="h-5 w-5" /> Tidy department names</DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : changes.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500" data-testid="tidy-dept-names-empty">All department names are already tidy.</p>
        ) : (
          <>
            <p className="text-sm text-slate-600" data-testid="tidy-dept-names-summary">
              {changes.length} departments will be renamed{data.merges ? `, ${data.merges} duplicates merged` : ""} — {data.products} products updated. This changes the names on the till, reports and the online shop.
            </p>
            <div className="max-h-[50vh] overflow-auto rounded border">
              <table className="w-full text-sm">
                <tbody>
                  {changes.map((c) => (
                    <tr key={`${c.category}-${c.new_name}`} className="border-b" data-testid={`tidy-row-${c.new_name}`}>
                      <td className="p-2 text-xs text-slate-500">{c.category || "—"}</td>
                      <td className="p-2 font-mono text-xs">{c.old_names.join(" + ")}</td>
                      <td className="p-2"><ArrowRight className="h-4 w-4 text-slate-400" /></td>
                      <td className="p-2 font-semibold">
                        {c.new_name} {c.drop_ids.length > 0 && <Badge className="ml-1 bg-blue-100 text-blue-800">merge</Badge>}
                      </td>
                      <td className="p-2 text-right text-xs text-slate-500">{c.products}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="tidy-dept-names-cancel">Cancel</Button>
          <Button onClick={apply} disabled={busy || changes.length === 0} data-testid="tidy-dept-names-apply">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Tidy {changes.length} names
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
